import axios from 'axios';

// Web Push for prayer times while the app is closed (Phase 2).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'falak.push.v1';
const DAY_MS = 24 * 60 * 60 * 1000;

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (_) { return {}; }
};
const write = (v) => {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (_) { /* ignore */ }
};

export const isPushEnabled = () => Boolean(read().enabled);

export const pushSupport = () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)
    || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
};

const b64ToBytes = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const sigOf = (p) => JSON.stringify([
  Number(p.lat).toFixed(3), Number(p.lon).toFixed(3), p.method, p.asr_method,
  p.fajr_offset_min, p.maghrib_offset_min, p.isha_offset_min, p.lang,
]);

async function getSubscription() {
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  const { data } = await axios.get(`${API}/push/public-key`);
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.publicKey) });
}

async function postSync(sub, p) {
  const json = sub.toJSON();
  await axios.post(`${API}/push/sync`, {
    subscription: { endpoint: json.endpoint, keys: json.keys },
    lang: p.lang,
    lat: p.lat,
    lon: p.lon,
    method: p.method,
    asr_method: p.asr_method,
    fajr_offset_min: p.fajr_offset_min,
    maghrib_offset_min: p.maghrib_offset_min,
    isha_offset_min: p.isha_offset_min,
    title_template: p.titleTemplate,
    body: p.body,
    names: p.names,
  });
}

export async function enablePush(params) {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(perm === 'denied' ? 'denied' : 'dismissed');
  const sub = await getSubscription();
  await postSync(sub, params);
  write({ enabled: true, endpoint: sub.endpoint, lastSync: Date.now(), sig: sigOf(params) });
}

const localSub = async () => {
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
};

// 'on' | 'off' | 'lost' (phone no longer allows notifications) | 'dropped' (browser silently cancelled the subscription)
export async function getPushState() {
  const st = read();
  if (!st.enabled || pushSupport() === 'unsupported') return 'off';
  if (Notification.permission !== 'granted') return 'lost';
  return st.dropped ? 'dropped' : 'on';
}

// Re-sends the schedule when settings/location/subscription change or once a day.
export async function syncPushIfNeeded(params) {
  const st = read();
  if (!st.enabled || pushSupport() !== 'ok' || Notification.permission !== 'granted') return;
  const sig = sigOf(params);
  const before = await localSub().catch(() => null);
  const sub = await getSubscription();
  const dropped = st.dropped || Boolean(st.endpoint && !before);
  if (st.sig === sig && st.endpoint === sub.endpoint && Date.now() - (st.lastSync || 0) < DAY_MS) return;
  await postSync(sub, params);
  write({ ...st, endpoint: sub.endpoint, lastSync: Date.now(), sig, dropped });
}

const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });

// Sends a real push to this phone, then checks the browser kept the subscription after receiving it.
export async function sendTestPush(params, title, body, onSent) {
  const sub = await getSubscription();
  await postSync(sub, params);
  write({ ...read(), enabled: true, endpoint: sub.endpoint, lastSync: Date.now(), sig: sigOf(params) });
  try {
    await axios.post(`${API}/push/test`, { endpoint: sub.endpoint, title, body });
  } catch (e) {
    const s = e.response && e.response.status;
    if (s === 404 || s === 410) throw new Error('dropped');
    if (s === 429) throw new Error('wait');
    throw e;
  }
  if (onSent) onSent();
  await wait(8000);
  const after = await localSub().catch(() => null);
  const dropped = !after || after.endpoint !== sub.endpoint;
  write({ ...read(), dropped });
  if (dropped) throw new Error('dropped');
}

export const pushBrowserName = () => (/SamsungBrowser/i.test(navigator.userAgent) ? 'Samsung Internet' : 'Chrome');

export async function disablePush() {
  const st = read();
  write({ enabled: false });
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    const endpoint = (sub && sub.endpoint) || st.endpoint;
    if (endpoint) await axios.post(`${API}/push/unsubscribe`, { endpoint });
    if (sub) await sub.unsubscribe();
  } catch (_) { /* best effort */ }
}

export function listenForegroundPush(onPush) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const handler = (e) => {
    if (e.data && e.data.type === 'falak-prayer-push') onPush(e.data);
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

export async function showForegroundNotification({ title, options }) {
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (reg) await reg.showNotification(title, options);
}
